/**
 * 智能体权限管理器
 * 负责 Agent 权限管理的相关业务逻辑
 * @author Joey
 */

import { useToast } from "@/hooks/use-toast";
import { addAgentUserAdmin } from "@/services/agent-v2-add-agent-user";
import { deleteAgentUser } from "@/services/agent-v2-delete-agent-user";
import { listAgentUsers, PermissionUserData } from "@/services/agent-v2-list-agent-user";
import { agentV2Stats, AgentV2StatsData } from "@/services/agent-v2-stats";
import { toggleAgentUserStatus } from "@/services/agent-v2-toggle-agent-user-status";
import { updateAgentAdmin } from "@/services/agent-v2-update-agent-user";
import { userPermissionDetail, UserPermissionDetailData } from "@/services/agent-v2-user-permission-detail";
import { canUseWindow } from "@/utils/canuseWindow";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import { iamAccountHooks, iamAccountManager } from "./iam-account-manager";
import { agentManager, agentsHooks } from "./agent-manager";
import { useUserManagementHooks } from "./agent-dashboard-user-management-manager";
import { WHITE_LIST_KEYS } from "@/components/agent-dashboard/constants";
import {
  agentOpsFeatureConfig,
  AgentOpsFeatureConfigData,
} from "@/services/agent-ops-feature-config";

// 缓存数据结构
/** 智能体权限管理映射 */
export const PERMISSION_MAP = {
  /** 用户管理 */
  user: 'userManagement',
  /** 权限管理 */
  permission: 'permissionManagement',
  /** 数据概览管理 */
  data: 'dataOverviewManagement',
  /** 内容管理 */
  content: 'contentManagement',
  /** 订单管理 */
  order: 'orderManagement',
  /** 企业资产库 */
  company: 'companyManagement',
  /** 操作日志 */
  log: 'logManagement',
} as const;

export type PermissionKey = keyof typeof PERMISSION_MAP;

/** 运营后台功能开关与侧边栏 permissionKey 映射 */
export const OPS_FEATURE_CONFIG_MAP: Record<
  PermissionKey,
  keyof AgentOpsFeatureConfigData
> = {
  user: "userManage",
  permission: "permissionManage",
  data: "dataOverview",
  content: "contentManage",
  order: "orderManage",
  company: "enterpriseAsset",
  log: "operationLog",
};

/** 创建/编辑管理员时可分配的权限（不含操作日志） */
export const CREATE_FORM_PERMISSION_KEYS = [
  "user",
  "permission",
  "data",
  "content",
  "order",
  "company",
] as const satisfies readonly PermissionKey[];

/** 判断指定菜单的功能开关是否开启 */
export const isOpsFeatureEnabled = (
  permissionKey: PermissionKey,
  config?: AgentOpsFeatureConfigData | null
) => !!config?.[OPS_FEATURE_CONFIG_MAP[permissionKey]];

/** 运营角色默认权限  */
const OPERATOR_DEFAULT_PERMISSION_KEYS: PermissionKey[] = [
  "user",
  "content",
  "order",
  "company",
];

/** 管理员角色默认权限 */
const ADMIN_DEFAULT_PERMISSION_KEYS: PermissionKey[] = [
  "user",
  "permission",
  "content",
  "order",
  "company",
];

type CreateFormPermissionValue = NonNullable<CreateAdminFormData["permissions"]>[number];

// 将权限 key 转换为权限值
const toPermissionValues = (
  keys: PermissionKey[],
  config?: AgentOpsFeatureConfigData | null
): CreateFormPermissionValue[] =>
  keys.filter((key) => isOpsFeatureEnabled(key, config))
    .map((key) => PERMISSION_MAP[key] as CreateFormPermissionValue);

// 运营角色默认权限（按功能开关过滤）
export const getOperatorDefaultPermissions = (
  config?: AgentOpsFeatureConfigData | null
) => toPermissionValues(OPERATOR_DEFAULT_PERMISSION_KEYS, config);

// 管理员角色默认权限（按功能开关过滤）
export const getAdminDefaultPermissions = (
  config?: AgentOpsFeatureConfigData | null
) => toPermissionValues(ADMIN_DEFAULT_PERMISSION_KEYS, config);

// 权限值与权限 key 的映射
const PERMISSION_VALUE_TO_KEY = Object.fromEntries(
  (Object.entries(PERMISSION_MAP) as [PermissionKey, string][]).map(
    ([key, value]) => [value, key]
  )
) as Record<string, PermissionKey>;

// 按功能开关过滤已选权限（去掉已关闭菜单）
export const filterPermissionsByOpsFeature = (
  permissions: CreateFormPermissionValue[] | undefined,
  config?: AgentOpsFeatureConfigData | null
): CreateFormPermissionValue[] => {
  if (!permissions?.length) return [];
  return permissions.filter((value) => {
    const key = PERMISSION_VALUE_TO_KEY[value];
    return key && isOpsFeatureEnabled(key, config);
  });
};

// 判断两组权限是否相同（顺序无关）
export const arePermissionsEqual = (arr1: string[], arr2: string[]) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((item) => arr2.includes(item));
};

/** 权限code */
const PERMISSION_CODE_MAP = {
  /** 无 0 */
  not: 0,
  /** 只读 1 */
  readonly: 1,
  /** 可编辑 2 */
  editable: 2,
  /** 所有权限 2*/
  all: 3,
} as const;

/** 角色类型: 1=运营, 2=管理员, 3=超级管理员, 4=付费用户 */
export const ROLE_TYPE_MAP = {
  /** 运营 */
  operator: 1,
  /** 管理员 */
  admin: 2,
  /** 超级管理员 */
  superAdmin: 3,
  /** 付费用户 */
  paidUser: 4,
} as const;

/** 角色状态: 1=启用, 2=禁用 */
export const ROLE_STATUS_MAP = {
  /** 启用 */
  enabled: 1,
  /** 禁用 */
  disabled: 0,
} as const;

export interface SearchFormData {
  searchType: string;
  inputText: string;
  roleStatus: number;
  roleType: number;
  page: number;
  pageSize: number;
  total?: number;
}

export interface CreateAdminFormData {
  id?: string;
  userSubId?: string;
  email: string;
  userName: string;
  /** 角色类型: 1=运营, 2=管理员, 3=超级管理员, 4=付费用户 */
  roleType: number;
  userManagement?: number;
  permissionManagement?: number;
  dataOverviewManagement?: number;
  contentManagement?: number;
  orderManagement?: number;
  companyManagement?: number;
  // 权限列表（前端处理）
  permissions?: ('userManagement' | 'permissionManagement' | 'dataOverviewManagement' | 'contentManagement' | 'orderManagement' | 'companyManagement')[];
}

type ListChangeEventDetail = { permissionList: PermissionUserData[] };
type ListChangeEvent = CustomEvent<ListChangeEventDetail>;

type SearchFormChangeEventDetail = SearchFormData
type SearchFormChangeEvent = CustomEvent<SearchFormChangeEventDetail>;

type CreateFormChangeEventDetail = CreateAdminFormData
type CreateFormChangeEvent = CustomEvent<CreateFormChangeEventDetail>;

type AgentV2StatsChangeEventDetail = AgentV2StatsData
type AgentV2StatsChangeEvent = CustomEvent<AgentV2StatsChangeEventDetail>;

type AgentOpsFeatureConfigChangeEventDetail = AgentOpsFeatureConfigData
type AgentOpsFeatureConfigChangeEvent = CustomEvent<AgentOpsFeatureConfigChangeEventDetail>;

type UserPermissionDetailChangeEventDetail = UserPermissionDetailData
type UserPermissionDetailChangeEvent = CustomEvent<UserPermissionDetailChangeEventDetail>;

export class AgentPermissionManager {
  private static instance: AgentPermissionManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_PERMISSION_LIST";
  // 缓存进行中的用户权限详情请求，避免并发重复请求
  private pendingUserPermissionDetailRequests = new Map<string, Promise<UserPermissionDetailData>>();
  // 当前 userPermissionDetail 对应的请求 key（agentId:userId）
  private userPermissionDetailRequestKey = "";
  // 当前智能体ID
  private agentId: string = "";
  private permissionList: PermissionUserData[] = [];
  // 当前用户的权限信息
  private userPermissionDetail: UserPermissionDetailData = null as UserPermissionDetailData;
  // 搜索表单的数据
  private searchForm: SearchFormData = {
    searchType: "",
    inputText: "",
    roleType: null,
    roleStatus: null,
    page: 1,
    pageSize: 10,
    total: 0,
  }
  // 智能体聚合统计数据
  private agentV2StatsData: AgentV2StatsData = {
    adminCount: 0,
    superAdminCount: 0,
    operatorCount: 0,
  };
  // 统计数据初始化状态
  private initializeStats = false;
  // 创建管理员的数据
  private createFormData: CreateAdminFormData = {
    id: "",
    email: "",
    userName: "",
    roleType: null,
    userManagement: null,
    permissionManagement: null,
    dataOverviewManagement: null,
    contentManagement: null,
    orderManagement: null,
    companyManagement: null,
    permissions: [],
  };
  // 已请求过功能开关配置的 agentId（用于本地缓存复用）
  private opsFeatureConfigFetchedAgentId = "";
  // 智能体运营后台功能开关配置
  private agentOpsFeatureConfigData: AgentOpsFeatureConfigData = {
    userManage: false,
    permissionManage: false,
    dataOverview: false,
    contentManage: false,
    orderManage: false,
    enterpriseAsset: false,
    operationLog: false,
  };

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentPermissionManager {
    if (!AgentPermissionManager.instance) {
      AgentPermissionManager.instance = new AgentPermissionManager();
    }
    return AgentPermissionManager.instance;
  }

  // 初始化
  public async init(agentId: string) {
    this.setAgentId(agentId);
    try {
      // 初始化时，如果用户列表为空，才请求数据
      if (this.getPermissionList().length === 0) {
        await this.fetchPermissionList();
      }
      // 初始化时，请求当前用户的权限信息
      // await this.fetchUserPermissionDetail({ agentId });
    } catch (error) {
      console.error("Failed to init agent permission list cache:", error);
      throw error;
    }
  }

  // 请求列表数据
  public async fetchPermissionList(page?: number) {
    try {
      const data = await listAgentUsers({
        ...this.getSearchForm(),
        agentId: this.agentId,
        page: page ?? this.searchForm.page,
      });
      this.setPermissionList(data.list);
      this.setSearchForm({
        ...this.searchForm,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
      });
    } catch (error) {
      console.error("Failed to fetch agent permission list:", error);
      this.dispatchPermissionListChange();
      throw error;
    }
  }

  /**
   * 请求用户权限信息
   * * 返回报错 1010，说明该用户既【未订阅】，也【没有运营平台权限】
   * @returns 
   */
  public async fetchUserPermissionDetail({ agentId, userId }: { agentId?: string, userId?: string }) {
    let requestKey: string = "";
    let isRequestOwner = false;

    try {
      const agentIdToUse = agentId || this.agentId || agentManager.getAgentId();
      const userIdToUse = userId || iamAccountManager.get()?.id;

      if (!agentIdToUse || !userIdToUse) {
        console.error("如果后面有请求，请忽略，agentId or userId is empty", { agentIdToUse, userIdToUse });
        return;
      }

      requestKey = `${agentIdToUse}:${userIdToUse}`;
      const pendingRequest = this.pendingUserPermissionDetailRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest;
      }

      isRequestOwner = true;
      const requestPromise = userPermissionDetail({
        agentId: agentIdToUse,
        userId: userIdToUse,
      });
      this.pendingUserPermissionDetailRequests.set(requestKey, requestPromise);

      const data = await requestPromise;

      this.userPermissionDetailRequestKey = requestKey;
      this.setUserPermissionDetail(data);

      return data;
    } catch (error) {
      if (error.code === 1010) {
        this.userPermissionDetailRequestKey = requestKey;
        this.setUserPermissionDetail({} as UserPermissionDetailData);
      }
      console.error("Failed to fetch user permission detail:", error);
      throw error;
    } finally {
      if (isRequestOwner && requestKey) {
        this.pendingUserPermissionDetailRequests.delete(requestKey);
      }
    }
  }

  // 请求智能体聚合统计数据
  public async fetchAgentV2Stats(agentId?: string) {
    try {
      const data = await agentV2Stats(agentId || this.agentId);
      this.setAgentV2StatsData(data);
    } catch (error) {
      console.error("Failed to fetch agent v2 stats:", error);
      this.dispatchAgentV2StatsChange();
      throw error;
    }
  }

  // 请求智能体运营后台功能开关配置（同一 agentId 已请求过会直接返回缓存，传 force 强制刷新）
  public async fetchAgentOpsFeatureConfig(
    agentId?: string,
    options?: { force?: boolean }
  ): Promise<AgentOpsFeatureConfigData> {
    const targetAgentId = agentId || this.agentId;
    if (
      !options?.force &&
      targetAgentId &&
      this.opsFeatureConfigFetchedAgentId === targetAgentId
    ) {
      return this.getAgentOpsFeatureConfigData();
    }
    try {
      const data = await agentOpsFeatureConfig({ agentId: targetAgentId });
      this.setAgentOpsFeatureConfigData(data);
      this.opsFeatureConfigFetchedAgentId = targetAgentId;
      return data;
    } catch (error) {
      console.error("Failed to fetch agent ops feature config:", error);
      this.dispatchAgentOpsFeatureConfigChange();
      throw error;
    }
  }

  // 创建管理员
  public async createAdmin(formData: CreateAdminFormData) {
    try {
      await addAgentUserAdmin({
        agentId: this.agentId,
        ...formData,
      });
    } catch (error) {
      console.error("Failed to create admin:", error);
      throw error;
    }
  }

  // 删除管理员
  async deleteAdmin(userId: string, roleType: number) {
    try {
      await deleteAgentUser({
        agentId: this.agentId,
        userId,
        roleType
      });
      // 刷新智能体聚合统计数据
      this.fetchAgentV2Stats();
    } catch (error) {
      console.error("Failed to delete admin:", error);
      throw error;
    }
  }

  // 切换智能体管理员启用禁用
  async toggleUserStatus(userId: string, roleType: number) {
    try {
      await toggleAgentUserStatus({
        agentId: this.agentId,
        userId,
        roleType,
      });
    } catch (error) {
      console.error("Failed to toggle user status:", error);
      throw error;
    }
  }

  // 更新智能体管理员
  async updateAgentAdmin(formData: Omit<CreateAdminFormData, 'agentId' | 'userId'>) {
    try {
      await updateAgentAdmin({
        agentId: this.agentId,
        userId: formData.id,
        ...formData,
      });
      // 刷新权限列表
      this.fetchPermissionList();
      // 刷新智能体聚合统计数据
      this.fetchAgentV2Stats();
    } catch (error) {
      console.error("Failed to update agent admin:", error);
      throw error;
    }
  }

  // 重置列表和搜索表单
  public resetPermissionListAndSearchForm() {
    this.setPermissionList([]);
    this.setSearchForm({
      searchType: '',
      inputText: '',
      roleStatus: null,
      roleType: null,
      page: 1,
      pageSize: 10,
    });
    // 重置统计数据
    this.setInitializeStats(false);
  }

  // ==================== 读取写入操作 ====================
  public getPermissionList() {
    return this.permissionList;
  }
  public setPermissionList(list: PermissionUserData[]) {
    this.permissionList = list;
    this.dispatchPermissionListChange();
  }

  public getAgentId(): string {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  // 搜索
  public getSearchForm() {
    return this.searchForm;
  }
  public setSearchForm(formData: SearchFormData) {
    this.searchForm = formData;
    this.dispatchSearchFormChange();
  }

  public getCreateFormData() {
    return this.createFormData;
  }
  public setCreateFormData(formData: CreateAdminFormData) {
    this.createFormData = formData;
    this.dispatchCreateFormChange();
  }

  // 智能体聚合统计
  public getAgentV2StatsData() {
    return this.agentV2StatsData;
  }
  public setAgentV2StatsData(data: AgentV2StatsData) {
    this.agentV2StatsData = data;
    this.dispatchAgentV2StatsChange();
  }

  // 运营后台功能开关配置
  public getAgentOpsFeatureConfigData() {
    return this.agentOpsFeatureConfigData;
  }
  public setAgentOpsFeatureConfigData(data: AgentOpsFeatureConfigData) {
    this.agentOpsFeatureConfigData = data;
    this.dispatchAgentOpsFeatureConfigChange();
  }

  // 当前用户的权限信息
  public getUserPermissionDetail() {
    return this.userPermissionDetail;
  }
  public hasCachedUserPermissionDetail({ agentId, userId }: { agentId?: string, userId?: string }) {
    const agentIdToUse = agentId || this.agentId || agentManager.getAgentId();
    const userIdToUse = userId || iamAccountManager.get()?.id;

    if (!agentIdToUse || !userIdToUse) return false;
    if (this.userPermissionDetail === null || this.userPermissionDetail === undefined) return false;

    const requestKey = `${agentIdToUse}:${userIdToUse}`;
    return this.userPermissionDetailRequestKey === requestKey;
  }
  public setUserPermissionDetail(data: UserPermissionDetailData) {
    if (data === null || data === undefined) {
      this.userPermissionDetailRequestKey = "";
    }
    this.userPermissionDetail = data;
    this.dispatchUserPermissionDetailChange();
  }

  public getInitializeStats() {
    return this.initializeStats;
  }
  public setInitializeStats(initializeStats: boolean) {
    this.initializeStats = initializeStats;
  }

  // ==================== 订阅通知操作 ====================
  // 监听列表变化
  private dispatchPermissionListChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<ListChangeEventDetail>(`${this.STORAGE_KEY}:list-change`, {
      detail: { permissionList: this.permissionList }
    });
    window.dispatchEvent(event);
  }
  public onPermissionListChange(listener: (e: ListChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:list-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听智能体聚合统计变化
  private dispatchAgentV2StatsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AgentV2StatsChangeEventDetail>(`${this.STORAGE_KEY}:agent-v2-stats-change`, {
      detail: this.agentV2StatsData
    });
    window.dispatchEvent(event);
  }
  public onAgentV2StatsChange(listener: (e: AgentV2StatsChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:agent-v2-stats-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听运营后台功能开关配置变化
  private dispatchAgentOpsFeatureConfigChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AgentOpsFeatureConfigChangeEventDetail>(`${this.STORAGE_KEY}:agent-ops-feature-config-change`, {
      detail: this.agentOpsFeatureConfigData
    });
    window.dispatchEvent(event);
  }
  public onAgentOpsFeatureConfigChange(listener: (e: AgentOpsFeatureConfigChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:agent-ops-feature-config-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  //  监听搜索表单变化
  private dispatchSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<SearchFormChangeEventDetail>(`${this.STORAGE_KEY}:search-form-change`, {
      detail: this.searchForm
    });
    window.dispatchEvent(event);
  }
  public onSearchFormChange(listener: (e: SearchFormChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:search-form-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听创建管理员表单变化
  private dispatchCreateFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<CreateFormChangeEventDetail>(`${this.STORAGE_KEY}:create-form-change`, {
      detail: this.createFormData
    });
    window.dispatchEvent(event);
  }
  public onCreateFormChange(listener: (e: CreateFormChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:create-form-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听用户权限信息变化
  private dispatchUserPermissionDetailChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<UserPermissionDetailChangeEventDetail>(`${this.STORAGE_KEY}:user-permission-detail-change`, {
      detail: this.userPermissionDetail
    });
    window.dispatchEvent(event);
  }
  public onUserPermissionDetailChange(listener: (e: UserPermissionDetailChangeEvent) => void) {
    const eventName = `${this.STORAGE_KEY}:user-permission-detail-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// ============= Hooks =============
// 权限列表数据
const usePermissionData = () => {
  const toast = useToast();
  const { id } = useParams();

  const [permissionList, setPermissionList] = useState(agentPermissionManager.getPermissionList());
  const [searchFormData, setSearchFormData] = useState(agentPermissionManager.getSearchForm());
  const [loading, setLoading] = useState(false);

  useUserManagementHooks.useListenerAfterAction('permission');

  const init = async () => {
    if (!id) {
      toast.toast({
        description: `Agent id is required`,
      });
      return;
    }
    try {
      setLoading(true);
      await agentPermissionManager.init(id);
      setLoading(false);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const search = async (page?: number) => {
    setLoading(true);
    try {
      await agentPermissionManager.fetchPermissionList(page);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  }

  const changeSearchForm = (key: keyof SearchFormData, value: string | number) => {
    agentPermissionManager.setSearchForm({
      ...searchFormData,
      [key]: value,
    });
  }

  const changePage = (page: number) => {
    changeSearchForm('page', page);
  }

  const resetAndSearch = async () => {
    agentPermissionManager.setSearchForm({
      searchType: null,
      inputText: "",
      roleType: null,
      roleStatus: null,
      page: 1,
      pageSize: 10,
    });
    await search();
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onPermissionListChange((e) => {
      setPermissionList(e.detail.permissionList)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onSearchFormChange((e) => {
      setSearchFormData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    permissionList,
    searchFormData,

    search,
    changeSearchForm,
    resetAndSearch,
    changePage,
  }
}

// 初始化用户权限详情数据
const useInitialPermissionDetail = () => {
  const toast = useToast();
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const { account } = iamAccountHooks.useData();

  const { navigateToFirstAgent } = agentsHooks.useAgentData();
  const { navigateToFirstAllowMenu } = useUserPermission();

  const isAgentUserManagement = useMatch("/agent/dashboard/user-management/:id/");
  const isAgentDashboardPermission = useMatch("/agent/dashboard/permission/:id/");
  const isAgentDashboardDataOverview = useMatch("/agent/dashboard/data-overview/:id/");
  const isAgentDashboardContentManagement = useMatch("/agent/dashboard/content-management/:id/");
  const isAgentDashboardOrderManagement = useMatch("/agent/dashboard/order-management/:id/");
  const isAgentDashboardCompanyAsset = useMatch("/agent/dashboard/company-asset/:id/");
  const isAgentDashboardLog = useMatch("/agent/dashboard/operation-log/:id/");

  // 是否是【用户管理】、【权限管理】、【数据概览】、【内容管理】、【订单管理】、【企业资产库】、【操作日志】页面
  const isDashboardPage = (
    isAgentUserManagement
    || isAgentDashboardPermission
    || isAgentDashboardDataOverview
    || isAgentDashboardContentManagement
    || isAgentDashboardOrderManagement
    || isAgentDashboardCompanyAsset
    || isAgentDashboardLog
  );

  // 页面路径 key
  const pathKey = useMemo(() => {
    if (isAgentUserManagement) return 'user';
    if (isAgentDashboardPermission) return 'permission';
    if (isAgentDashboardDataOverview) return 'data';
    if (isAgentDashboardContentManagement) return 'content';
    if (isAgentDashboardOrderManagement) return 'order';
    if (isAgentDashboardCompanyAsset) return 'company';
    if (isAgentDashboardLog) return 'log';
    return null;
  }, [isAgentUserManagement, isAgentDashboardPermission, isAgentDashboardDataOverview, isAgentDashboardContentManagement, isAgentDashboardOrderManagement, isAgentDashboardCompanyAsset, isAgentDashboardLog]);

  const init = async () => {
    try {
      const isDashboardRoute = location.pathname.startsWith('/agent/dashboard');
      const userIdToUse = account?.id || JSON.parse(localStorage.getItem('MIUTA_ACCOUNT') || '{}')?.id;

      if (!isDashboardRoute && agentPermissionManager.hasCachedUserPermissionDetail({
        agentId: params.id,
        userId: userIdToUse,
      })) {
        return;
      }

      // 并行请求：用户权限 + 运营后台功能开关配置
      const [data, opsConfig] = await Promise.all([
        agentPermissionManager.fetchUserPermissionDetail({
          agentId: params.id,
          userId: userIdToUse,
        }),
        agentPermissionManager.fetchAgentOpsFeatureConfig(params.id),
      ]);

      // 处于运营后台页面进行权限判断
      if (isDashboardPage) {
        // 取出当前页面的用户权限等级
        const permissionField = pathKey ? PERMISSION_MAP[pathKey] : null;
        const permissionLevel = permissionField ? (data?.[permissionField] ?? PERMISSION_CODE_MAP.not) : PERMISSION_CODE_MAP.not;
        // 取出当前页面的功能开关状态
        const featureField = pathKey ? OPS_FEATURE_CONFIG_MAP[pathKey] : null;
        const featureEnabled = featureField ? !!opsConfig?.[featureField] : false;

        // 判断一：如果用户角色权限状态为 disabled ，则跳转至第一个 agent 的详情页
        if (data.roleStatus === ROLE_STATUS_MAP.disabled) {
          toast.error({
            description: t("agent_dashboard.permission.no_admin_permission"),
          });
          navigateToFirstAgent();
        }

        // 白名单页面，直接返回
        if (WHITE_LIST_KEYS.includes(pathKey)) return;

        // 判断二：当前菜单被大后台禁用，跳转至第一个允许访问的菜单
        if (!featureEnabled) {
          navigateToFirstAllowMenu({ data, opsConfig, next: navigateToFirstAgent });
          return;
        }

        // 判断三：当前菜单用户没有权限访问，跳转至第一个允许访问的菜单
        if (permissionLevel === PERMISSION_CODE_MAP.not) {
          navigateToFirstAllowMenu({ data, opsConfig, next: navigateToFirstAgent });
          return;
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    // 在 dashboard 路由下切换页面时，清空旧数据并重新校验权限
    if (location.pathname.startsWith('/agent/dashboard')) {
      agentPermissionManager.setUserPermissionDetail(null);
    }
    // 初始化权限
    init();
  }, [params.id, location.pathname]);

  useEffect(() => {
    if (account.id && !agentPermissionManager.getUserPermissionDetail()) {
      init();
    }
  }, [account.id]);
}

/**
 * !当前用户的智能体的订阅状态和运营平台权限
 * * 当 userPermissionDetail 为空{}，说明该用户既【未订阅】，也【没有运营平台权限】
 * 
 * * 【订阅状态判断】：
 * * 情况一：正常返回数据，但是 order_user 字段为 null，说明该用户被删除，订阅状态为【未订阅】
 * * 情况二：正常返回数据，但是 order_user 字段中的 role_status 为 0，说明该用户被禁用，订阅状态变为【异常】
 * * 情况三：未超过清除记录日期
 * 
 * *【运营平台权限判断】
 * * 情况一：正常返回数据，但是 role_status 字段为 0，说明该用户【无权限】
 * * 情况二：正常返回数据，但是 五个菜单字段（PERMISSION_MAP），都为 0，说明该用户【无权限】
 * @returns 
 */
const useUserPermission = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const agentId = useParams().id;

  const [userPermissionDetail, setUserPermissionDetail] = useState(
    agentPermissionManager.getUserPermissionDetail()
  );
  const [opsFeatureConfigData, setOpsFeatureConfigData] = useState(
    agentPermissionManager.getAgentOpsFeatureConfigData()
  );

  // 测试数据，用来测试路由跳转
  // const userPermissionDetail = {
  //   userManagement: PERMISSION_CODE_MAP.not,
  //   permissionManagement: PERMISSION_CODE_MAP.not,
  //   dataOverviewManagement: PERMISSION_CODE_MAP.not,
  //   contentManagement: PERMISSION_CODE_MAP.not,
  //   orderManagement: PERMISSION_CODE_MAP.readonly,
  // }

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onUserPermissionDetailChange((e) => {
      setUserPermissionDetail(e.detail);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onAgentOpsFeatureConfigChange((e) => {
      setOpsFeatureConfigData(e.detail);
    });
    return unsubscribe;
  }, []);

  // 获取指定权限值
  const getPermissionLevel = (key: PermissionKey) =>
    userPermissionDetail?.[PERMISSION_MAP[key]] ?? PERMISSION_CODE_MAP.not;

  // 功能开关是否开启（大后台配置）
  const isFeatureEnabled = (key: PermissionKey) =>
    !!opsFeatureConfigData?.[OPS_FEATURE_CONFIG_MAP[key]];

  // 菜单是否可访问（先看功能开关，再看用户权限）
  const canAccessMenu = (key: PermissionKey) =>
    isFeatureEnabled(key) && getPermissionLevel(key) !== PERMISSION_CODE_MAP.not;

  // 按钮是否可点击（2或3）
  const canClick = (key: PermissionKey) => {
    const level = getPermissionLevel(key);
    return level === PERMISSION_CODE_MAP.editable || level === PERMISSION_CODE_MAP.all;
  };

  // 是否只读（1）
  const isReadonly = (key: PermissionKey) =>
    getPermissionLevel(key) === PERMISSION_CODE_MAP.readonly;

  // 是否可编辑（2）
  const isEditable = (key: PermissionKey) =>
    getPermissionLevel(key) === PERMISSION_CODE_MAP.editable;

  // 是否有所有权限（3） 
  const isAll = (key: PermissionKey) =>
    getPermissionLevel(key) === PERMISSION_CODE_MAP.all;

  // 导航到允许访问的第一个菜单（同时满足：菜单开关开启 + 用户有权限）
  const navigateToFirstAllowMenu = (props: {
    data?: UserPermissionDetailData,
    opsConfig?: AgentOpsFeatureConfigData,
    newWindow?: boolean,
    next?: () => void
  } = {}) => {
    const { data, opsConfig, newWindow, next } = props || {};

    const detail = data ?? agentPermissionManager.getUserPermissionDetail();
    const config = opsConfig ?? agentPermissionManager.getAgentOpsFeatureConfigData();

    const allowMenuKeys = Object.keys(PERMISSION_MAP) as PermissionKey[];
    const firstAllowMenu = allowMenuKeys.find((menuKey) => {
      // 1. 菜单开关未开启 → 跳过
      if (!config?.[OPS_FEATURE_CONFIG_MAP[menuKey]]) return false;
      // 2. 白名单菜单（如：操作日志）只校验功能开关，不校验用户权限
      if (WHITE_LIST_KEYS.includes(menuKey)) return true;
      // 3. 用户没有权限 → 跳过
      const level = (detail?.[PERMISSION_MAP[menuKey]] ?? PERMISSION_CODE_MAP.not);
      return level !== PERMISSION_CODE_MAP.not;
    });

    const routeKeyMap = {
      user: 'user-management',
      permission: 'permission',
      data: 'data-overview',
      content: 'content-management',
      order: 'order-management',
      company: 'company-asset',
      log: 'operation-log',
    };

    // userPermissionDetail 为空，或者没有可访问菜单，或角色被禁用，则提示无权限
    if (JSON.stringify(detail) === '{}' || !firstAllowMenu || detail?.roleStatus === ROLE_STATUS_MAP.disabled) {
      toast.toast({
        description: t("agent_dashboard.permission.no_admin_permission"),
      });
      next?.();
      return;
    }

    // 新窗口打开
    if (newWindow) {
      window.open(`/agent/dashboard/${routeKeyMap[firstAllowMenu]}/${agentId}/`, '_blank');
      return;
    }

    navigate(`/agent/dashboard/${routeKeyMap[firstAllowMenu]}/${agentId}/`);
  }

  // 判断订阅状态
  const subscriptionState = useMemo(() => {
    const initSubscribed = !userPermissionDetail || JSON.stringify(userPermissionDetail) === '{}'
    return {
      // 已订阅（是订单用户 && 用户未被删除 && 未超过清除记录日期）
      subscribed: initSubscribed ||
        JSON.stringify(userPermissionDetail) !== '{}'
        && userPermissionDetail?.orderUser
        && new Date(userPermissionDetail?.orderUser?.clearRecordAt || '').getTime() > Date.now(),
      // 异常（被禁用）
      isException: userPermissionDetail?.orderUser?.roleStatus === ROLE_STATUS_MAP.disabled,
    }
  }, [userPermissionDetail, userPermissionDetail?.orderUser])

  // 判断是否为管理员（运营平台权限）
  const isPlatformAdmin = useMemo(() => {
    return JSON.stringify(userPermissionDetail) !== '{}' &&
      userPermissionDetail?.roleStatus &&
      userPermissionDetail?.roleStatus !== ROLE_STATUS_MAP.disabled;
  }, [userPermissionDetail, userPermissionDetail?.roleStatus])

  return {
    userPermissionDetail,
    subscriptionState,
    getPermissionLevel,
    canAccessMenu,
    canClick,
    isReadonly,
    isEditable,
    isAll,
    isPlatformAdmin,
    navigateToFirstAllowMenu,
  };
};

// 当前用户的角色类型
const useUserRole = () => {
  const [userPermissionDetail, setUserPermissionDetail] = useState(
    agentPermissionManager.getUserPermissionDetail()
  );

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onUserPermissionDetailChange((e) => {
      setUserPermissionDetail(e.detail);
    });
    return unsubscribe;
  }, []);

  const roleType = userPermissionDetail?.roleType;

  // 编辑，启用，禁用，删除的权限控制
  // 超级管理员可以对 管理员、运营 进行管理
  // 管理员可以对 运营 进行管理
  // 运营只能查看，不能进行管理
  const canManageRole = useCallback((targetRoleType: number): boolean => {
    // 角色等级映射表（数字越小，等级越高）
    const roleLevelMap = {
      [ROLE_TYPE_MAP.superAdmin]: 1,  // 最高等级
      [ROLE_TYPE_MAP.admin]: 2,
      [ROLE_TYPE_MAP.operator]: 3,
      [ROLE_TYPE_MAP.paidUser]: 4,    // 最低等级
    };

    // 如果当前用户没有角色类型，返回false
    if (!roleType) return false;

    // 获取当前用户和目标用户的角色等级
    const currentLevel = roleLevelMap[roleType];
    const targetLevel = roleLevelMap[targetRoleType];

    // 只有当当前用户的角色等级低于目标用户的角色等级时（即等级数字更小），才能进行管理操作
    // 付费用户不能管理任何角色
    return currentLevel < targetLevel && currentLevel <= 3;
  }, [roleType]);

  return {
    roleType,
    canManageRole
  }
}

// 运营后台功能开关配置
const useAgentOpsFeatureConfig = () => {
  const toast = useToast();

  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [agentOpsFeatureConfigData, setAgentOpsFeatureConfigData] = useState(
    agentPermissionManager.getAgentOpsFeatureConfigData()
  );

  const init = async () => {
    if (!id) {
      toast.toast({ description: `Agent id is required` });
      return;
    }
    agentPermissionManager.setAgentId(id);
    try {
      setLoading(true);
      await agentPermissionManager.fetchAgentOpsFeatureConfig(id);
    } catch (error) {
      console.log(error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, [id]);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = agentPermissionManager.onAgentOpsFeatureConfigChange((e) => {
      setAgentOpsFeatureConfigData(e.detail);
      if (Object.keys(e.detail).length > 0) {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  return {
    loading,
    agentOpsFeatureConfigData,
  };
};

// agent聚合统计
const useAgentV2Stats = () => {
  const toast = useToast();

  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [agentV2StatsData, setAgentV2StatsData] = useState(agentPermissionManager.getAgentV2StatsData());

  const init = async () => {
    if (!id) {
      toast.toast({
        description: `Agent id is required`,
      });
      return;
    }
    agentPermissionManager.setAgentId(id);
    try {
      setLoading(true);
      // 初始化统计数据
      agentPermissionManager.setInitializeStats(true);
      await agentPermissionManager.fetchAgentV2Stats(id);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // 如果已经初始化了统计数据，就不需要再初始化
    if (agentPermissionManager.getInitializeStats()) return;
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onAgentV2StatsChange((e) => {
      setAgentV2StatsData(e.detail)
    })
    return unsubscribe
  }, [])

  return {
    loading,
    agentV2StatsData,
  }
}

// 创建/更新管理员
const useCreateAndUpdateAdmin = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateAdminFormData>(agentPermissionManager.getCreateFormData());
  const [customPermission, setCustomPermission] = useState('0');
  const [opsFeatureConfigData, setOpsFeatureConfigData] = useState(
    agentPermissionManager.getAgentOpsFeatureConfigData()
  );

  const {
    userNameError,
    emailError,
    roleTypeError,
    resetError,
    validateMap,
    validateField,
    validateAll
  } = useValidateFormData();

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onAgentOpsFeatureConfigChange((e) => {
      setOpsFeatureConfigData(e.detail);
    });
    return unsubscribe;
  }, []);

  // 角色默认权限（按功能开关过滤）
  const operatorPermissions = useMemo(
    () => getOperatorDefaultPermissions(opsFeatureConfigData),
    [opsFeatureConfigData]
  );

  const adminPermissions = useMemo(
    () => getAdminDefaultPermissions(opsFeatureConfigData),
    [opsFeatureConfigData]
  );

  // 校验数据
  const validate = (): boolean => {
    // 调接口前的兜底校验
    const valid = validateAll(createFormData);
    if (!valid) return false;

    // 校验是否选择了权限
    if (!createFormData.permissions.length) {
      toast.toast({
        description: t("agent_dashboard.permission.create_modal.permissions_empty"),
      });
      return false;
    }

    return true;
  }

  // 创建管理员
  const createAdmin = async () => {
    if (!validate()) return;

    setLoading(true);
    try {

      // 将权限转为接口需要的格式，默认给所有权限
      const transformPermission = Object.fromEntries(
        createFormData.permissions.map(key => [key, PERMISSION_CODE_MAP.all])
      );

      // 整理请求参数
      const params = {
        userName: createFormData.userName,
        email: createFormData.email,
        roleType: createFormData.roleType,
        ...transformPermission,
      }

      await agentPermissionManager.createAdmin(params);

      // 刷新权限列表和统计数据
      Promise.all([
        agentPermissionManager.fetchPermissionList(),
        agentPermissionManager.fetchAgentV2Stats(),
      ])

      return true
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  // 更新管理员
  const updateAdmin = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      // 将权限转为接口需要的格式，默认给所有权限
      const transformPermission = Object.fromEntries(
        createFormData.permissions.map(key => [key, PERMISSION_CODE_MAP.all])
      );

      // 整理请求参数
      const params = {
        id: createFormData.userSubId,
        userName: createFormData.userName,
        email: createFormData.email,
        roleType: createFormData.roleType,
        ...transformPermission,
      }

      await agentPermissionManager.updateAgentAdmin({
        ...params,
      });

      // 刷新权限列表和统计数据
      Promise.all([
        agentPermissionManager.fetchPermissionList(),
        agentPermissionManager.fetchAgentV2Stats(),
        agentPermissionManager.fetchUserPermissionDetail({}),
      ])

      return true
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  // 设置详情数据到表单
  const setDetailToCreateFormData = (data: CreateAdminFormData) => {
    // 去掉已关闭功能对应的权限，再写入表单
    const permissions = filterPermissionsByOpsFeature(
      data.permissions,
      opsFeatureConfigData
    );
    const formData = { ...data, permissions };

    // 与当前功能开关下的角色默认权限比较，判断是否为自定义权限
    const isCustomPermission =
      !arePermissionsEqual(permissions, operatorPermissions) &&
      !arePermissionsEqual(permissions, adminPermissions);
    const nextCustomPermission = isCustomPermission ? '1' : '0';

    // 功能开关加载后会再次同步，避免相同数据重复写入
    if (
      createFormData.id === data.id &&
      arePermissionsEqual(createFormData.permissions ?? [], permissions) &&
      customPermission === nextCustomPermission
    ) {
      return;
    }

    agentPermissionManager.setCreateFormData(formData);
    setCustomPermission(nextCustomPermission);
  }

  // 修改表单数据
  const changeValue = (key: keyof CreateAdminFormData, value: string | number | string[]) => {
    // 输入即校验数据
    validateField(key, value);

    // 角色类型改变时，更新权限
    if (key === 'roleType') {
      // 角色类型改变时，自定义权限默认不开启
      setCustomPermission('0');
      if (value === ROLE_TYPE_MAP.operator) {
        agentPermissionManager.setCreateFormData({
          ...createFormData,
          roleType: value,
          permissions: operatorPermissions,
        });
        return;
      } else if (value === ROLE_TYPE_MAP.admin) {
        agentPermissionManager.setCreateFormData({
          ...createFormData,
          roleType: value,
          permissions: adminPermissions,
        });
        return;
      }
    }

    agentPermissionManager.setCreateFormData({
      ...createFormData,
      [key]: value,
    });
  }

  // 自定义权限改变时，更新表单数据
  const onCustomPermissionChange = (value: string) => {
    setCustomPermission(value);
    // 自定义权限关闭时，更新为角色的对应权限
    if (value === '0') {
      if (createFormData.roleType === ROLE_TYPE_MAP.operator) {
        agentPermissionManager.setCreateFormData({
          ...createFormData,
          permissions: operatorPermissions,
        });
      } else if (createFormData.roleType === ROLE_TYPE_MAP.admin) {
        agentPermissionManager.setCreateFormData({
          ...createFormData,
          permissions: adminPermissions,
        });
      }
    }
  }

  // 清除表单数据
  const clearCreateFormData = () => {
    agentPermissionManager.setCreateFormData({} as CreateAdminFormData);
    resetError();
    setCustomPermission('0');
  }

  useEffect(() => {
    const unsubscribe = agentPermissionManager.onCreateFormChange((e) => {
      setCreateFormData(e.detail);
    });
    return unsubscribe
  }, []);

  return {
    loading,
    createFormData,
    userNameError,
    emailError,
    roleTypeError,
    customPermission,
    opsFeatureConfigData,

    onCustomPermissionChange,
    createAdmin,
    updateAdmin,
    clearCreateFormData,
    setDetailToCreateFormData,
    changeValue,
  }
}

// 校验表单数据
const useValidateFormData = () => {
  const { t } = useTranslation();
  const [userNameError, setUserNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [roleTypeError, setRoleTypeError] = useState('');

  // 校验 email
  const validateEmail = (value: string) => {
    // 检查格式：邮箱中必须包含@符号、@符号前后必须有字符、域名部分必须包含至少一个点、不允许包含空白字符
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError(t('agent_dashboard.permission.create_modal.user_email_help_text'));
      return false;;
    } else {
      setEmailError('');
      return true;
    }
  }

  // 校验 username
  const validateName = (value: string) => {
    if (!value || value.length < 2 || value.length > 20) {
      setUserNameError(t('agent_dashboard.permission.create_modal.name_placeholder'));
      return false;
    } else {
      setUserNameError('');
      return true;
    }
  }

  // 校验角色类型
  const validateRoleType = (value: number) => {
    if (!value) {
      setRoleTypeError(t('agent_dashboard.permission.create_modal.role_placeholder'));
      return false;
    } else {
      setRoleTypeError('');
      return true;
    }
  }

  // 重置校验错误
  const resetError = () => {
    setUserNameError('');
    setEmailError('');
    setRoleTypeError('');
  }

  const validateMap = {
    userName: validateName,
    email: validateEmail,
    roleType: validateRoleType,
  }

  const validateField = (name: keyof CreateAdminFormData, value: string | number | string[]) => {
    if (!validateMap[name]) return;
    return validateMap[name](value);
  }

  // 校验全部表单数据
  const validateAll = (formData: CreateAdminFormData) => {
    return [
      validateName(formData.userName),
      validateEmail(formData.email),
      validateRoleType(formData.roleType),
    ].every(Boolean);
  }

  return {
    userNameError,
    emailError,
    roleTypeError,

    validateMap,
    validateField,
    validateAll,
    resetError,
  }
}

// 删除智能体管理员
const useDeleteAdmin = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const deleteAdmin = async (userId: string, roleType: number) => {
    setLoading(true);
    try {
      await agentPermissionManager.deleteAdmin(userId, roleType);
      await agentPermissionManager.fetchPermissionList();
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    deleteAdmin,
  }
}

// 切换智能体管理员启用禁用
const useToggleUserStatus = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const toggleUserStatus = async (userId: string, roleType: number) => {
    setLoading(true);
    try {
      await agentPermissionManager.toggleUserStatus(userId, roleType);
      await agentPermissionManager.fetchPermissionList();
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    toggleUserStatus,
  }
}

export const agentPermissionHooks = {
  usePermissionData,
  useInitialPermissionDetail,
  useUserPermission,
  useUserRole,
  useAgentOpsFeatureConfig,
  useAgentV2Stats,
  useCreateAndUpdateAdmin,
  useDeleteAdmin,
  useToggleUserStatus,
}

// 导出单例实例
export const agentPermissionManager = AgentPermissionManager.getInstance();